// Holiday calendar management settings component

import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Trash2, Upload, Edit2, Check, X } from 'lucide-react';
import { useHolidays } from '../HolidayProvider';
import {
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
  DEFAULT_CALENDAR_ID,
} from '../constants';
import { Calendar } from '../types';

export function HolidaySettings() {
  const {
    calendars,
    addCalendar,
    removeCalendar,
    toggleCalendar,
    renameCalendar,
  } = useHolidays();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingCalendarId, setEditingCalendarId] = useState<string | null>(
    null,
  );
  const [editingName, setEditingName] = useState('');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ACCEPTED_FILE_TYPES.includes(`.${extension}` as any)) {
      toast.error(
        `Invalid file type. Please upload ${ACCEPTED_FILE_TYPES.join(' or ')} files.`,
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
      );
      return;
    }

    setIsUploading(true);
    try {
      await addCalendar(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      // Error already handled by addCalendar (toast shown)
      console.error('Failed to upload calendar:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = (id: string) => {
    if (id === DEFAULT_CALENDAR_ID) {
      toast.error('Cannot remove default calendar');
      return;
    }
    removeCalendar(id);
  };

  const handleToggle = (id: string, enabled: boolean) => {
    toggleCalendar(id, enabled);
  };

  const startEditing = (calendar: Calendar) => {
    setEditingCalendarId(calendar.id);
    setEditingName(calendar.name);
  };

  const cancelEditing = () => {
    setEditingCalendarId(null);
    setEditingName('');
  };

  const saveEditing = () => {
    if (!editingCalendarId || !editingName.trim()) {
      toast.error('Calendar name cannot be empty');
      return;
    }

    renameCalendar(editingCalendarId, editingName);
    cancelEditing();
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="space-y-3">
        <div>
          <Label htmlFor="calendar-upload" className="text-base font-medium">
            Upload Holiday Calendar
          </Label>
          <p className="text-muted-foreground mt-1 text-sm">
            Upload an iCal file (.ics or .ical) to add custom holidays to your
            calendar. Maximum file size: 1MB.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            ref={fileInputRef}
            id="calendar-upload"
            type="file"
            accept=".ics,.ical"
            onChange={handleFileSelect}
            disabled={isUploading}
            className="flex-1"
          />
          <Button
            variant="outline"
            size="icon"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar List */}
      <div className="space-y-3">
        <Label className="text-base font-medium">Your Holiday Calendars</Label>
        {calendars.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No calendars added yet. Upload an iCal file to get started.
          </p>
        ) : (
          <div className="space-y-2">
            {calendars.map((calendar) => (
              <div
                key={calendar.id}
                className="border-input flex items-center gap-3 rounded-lg border p-3"
              >
                {/* Color Indicator */}
                <div
                  className="size-6 shrink-0 rounded-full"
                  style={{ backgroundColor: calendar.color }}
                />

                {/* Calendar Name (Editable) */}
                <div className="flex-1">
                  {editingCalendarId === calendar.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditing();
                          if (e.key === 'Escape') cancelEditing();
                        }}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={saveEditing}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={cancelEditing}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{calendar.name}</span>
                      {calendar.source === 'default' && (
                        <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs">
                          Default
                        </span>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => startEditing(calendar)}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    checked={calendar.enabled}
                    onCheckedChange={(checked) =>
                      handleToggle(calendar.id, checked)
                    }
                  />
                  <span className="text-muted-foreground text-sm">
                    {calendar.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                {/* Remove Button (not for default) */}
                {calendar.source !== 'default' && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(calendar.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-muted-foreground text-sm">
          <strong>Tip:</strong> Holidays from enabled calendars will be
          displayed in date pickers with colored backgrounds. Each calendar is
          assigned a unique color automatically.
        </p>
      </div>
    </div>
  );
}
